import { rootEngine } from "./engines.js";
import { selectRun, selectRunTree } from "./selectors.js";

/**
 * Which root a timeline row belongs to, from the `_rowId` stamped on a clicked event — and
 * the engine a branch made from that row will run on.
 *
 * Rows carry "compare-<laneIndex>" for a compare lane, "branch-<branchRunId>" for a
 * sub-branch row (branch run ids are globally unique, so every candidate root — the
 * focused one plus every compare lane — is scanned for the owner), and anything else for
 * the main spine. Falls back to `active` when a lane is gone or no root owns the branch,
 * so a caller always has SOME root to read the branch list from.
 *
 * `branchEngine` is resolved the way `useBranchForecast` gates and `branchForecast.ts`
 * dispatches: from the run the branch will PARENT off — the clicked BRANCH run on a branch
 * row (its own durable `backend`; a branch minted before inheritance, #1980, may carry a
 * different engine from its root), else the root — then the root as a last resort. Null when
 * nothing is known. Mirrors the geometry `useBranchForecast.handleBranch` resolves inline
 * (left verbatim there — a §7 booby-trap block).
 */
export function resolveClickedRoot(rowId, { active, compareRoots, runState }) {
  const rid = rowId || "";
  const laneMatch = /^compare-(\d+)$/.exec(rid);
  const branchMatch = /^branch-(.+)$/.exec(rid);
  const clickedBranchId = branchMatch ? branchMatch[1] : null;
  const lanes = compareRoots || [];
  let root = laneMatch ? lanes[Number(laneMatch[1])] || null : null;
  if (!root && clickedBranchId != null) {
    root =
      [active, ...lanes].find(
        (r) =>
          r &&
          r.id &&
          selectRunTree(runState, r.id)
            .slice(1)
            .some((b) => b.runId === clickedBranchId),
      ) || null;
  }
  const resolvedRoot = root || active || null;
  const parentRun =
    clickedBranchId != null
      ? selectRun(runState, clickedBranchId)
      : resolvedRoot?.id
        ? selectRun(runState, resolvedRoot.id)
        : null;
  const branchEngine = rootEngine(parentRun) || rootEngine(resolvedRoot) || null;
  return { root: resolvedRoot, clickedBranchId, branchEngine };
}
